import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../components/ui/ui-button';
import { cardStyles, motionStyles, pageFrameStyles } from '../styles/lit-shared';

const CALENDAR_URL = 'https://calendar.app.google/3TMX3h7SGT1wkdp87';

const STEPS = [
  {
    title: 'Creas tu perfil',
    body: 'Eliges el look: color, estilo de botón, fondo y tipografía. La página se siente tuya desde el primer minuto, en móvil y en escritorio.',
  },
  {
    title: 'Añades enlaces y los ordenas',
    body: 'Tantos botones como necesites. Los reordenas arrastrando y publicas al instante. Sin colas ni “guardar y esperar”.',
  },
  {
    title: 'Montas secciones enriquecidas',
    body: 'Mezclas texto, imágenes y vídeos embed (YouTube, Vimeo) con un CTA. Layout a izquierda o a derecha, tú decides.',
  },
  {
    title: 'Capturas audiencia y mides',
    body: 'Formulario de email integrado y analítica de la página. Tu lista no depende del algoritmo de nadie.',
  },
  {
    title: 'Compartes con SEO listo',
    body: 'Título, descripción e imagen Open Graph personalizados. Cuando alguien pega tu URL, se ve como tu marca, no como un enlace genérico.',
  },
] as const;

const FEATURES = [
  {
    title: 'Perfil único',
    body: 'Tu marca, tu estilo, tu look. Elegante y adaptable a cualquier dispositivo.',
  },
  {
    title: 'Enlaces ilimitados',
    body: 'Añade tantos botones como necesites, reordénalos arrastrando y publica al instante.',
  },
  {
    title: 'Secciones enriquecidas',
    body: 'Mezcla texto, imágenes y vídeos embed (YouTube, Vimeo) con un CTA. Layout izquierda o derecha.',
  },
  {
    title: 'Iconos sociales nativos',
    body: 'Instagram, TikTok, YouTube, X, LinkedIn, Threads, Spotify… todos con sus colores reales.',
  },
  {
    title: 'Captura de suscriptores',
    body: 'Formulario de email integrado para que tu audiencia no dependa del algoritmo de nadie.',
  },
  {
    title: 'Temas y branding',
    body: 'Color principal, estilo de botón, fondo de tarjeta, color de texto. Haz tuyo cada píxel.',
  },
  {
    title: 'Biblioteca de assets',
    body: 'Sube imágenes y se sirven al instante desde una red global. Sin configuración.',
  },
  {
    title: 'Listo para SEO',
    body: 'Título, descripción e imagen Open Graph personalizadas para que tu página luzca al compartirla.',
  },
] as const;

const NOT_THIS = [
  'No es una lista de botones con la misma plantilla que usa medio internet.',
  'No te ata al algoritmo de Instagram, TikTok o X para llegar a tu gente.',
  'No es un CMS generalista: es una página de marca, no un blog disfrazado.',
  'No pide configurar CDN, DNS ni hosting para que las imágenes vuelen.',
] as const;

const COMPARE = [
  { without: 'Bio genérica que parece de todos', with: 'Una página que se siente como tuya' },
  { without: 'Cuatro botones y a conformarse', with: 'Enlaces ilimitados, reordenables al instante' },
  { without: 'Solo links', with: 'Texto, imagen, vídeo embed y CTA' },
  { without: 'Iconos grises de paquete', with: 'Iconos sociales con sus colores reales' },
  { without: 'Audiencia atrapada en la red de turno', with: 'Suscriptores en tu lista' },
  { without: 'Vista previa fea al compartir', with: 'Título, descripción e imagen Open Graph propios' },
] as const;

@customElement('traviapp-page')
export class TraviappPage extends LitElement {
  static styles = [
    pageFrameStyles,
    cardStyles,
    motionStyles,
    css`
      .hero {
        position: relative;
        margin-bottom: var(--space-2xl);
        padding: var(--space-2xl) var(--space-xl);
        border-radius: var(--radius-lg);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        overflow: hidden;
        text-align: center;
      }

      .hero::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -10%;
        width: 300px;
        height: 300px;
        border-radius: 50%;
        background: var(--glow-cyan);
        filter: blur(50px);
        pointer-events: none;
      }

      .hero-content {
        position: relative;
        z-index: 1;
      }

      .eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        margin-bottom: var(--space-md);
        padding: 0.25rem 0.75rem;
        border-radius: var(--radius-full);
        background: rgb(255 255 255 / 6%);
        border: 1px solid var(--color-border);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-accent);
      }

      h1 {
        font-size: clamp(2rem, 5vw, 3rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        margin: 0 0 var(--space-md);
        color: var(--color-text);
      }

      .brand-name {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .tagline {
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--color-primary-light);
        margin: 0 0 var(--space-md);
      }

      .hero-lead {
        font-size: 1.125rem;
        color: var(--color-text-muted);
        max-width: 52ch;
        margin: 0 auto var(--space-xl);
        line-height: 1.6;
      }

      .hero-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-md);
        justify-content: center;
      }

      section {
        margin-bottom: var(--space-2xl);
      }

      .section-title {
        font-size: clamp(1.5rem, 3vw, 1.875rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        margin: 0 0 var(--space-sm);
      }

      .section-lead {
        color: var(--color-text-muted);
        font-size: 1rem;
        margin: 0 0 var(--space-xl);
        max-width: 56ch;
        line-height: 1.6;
      }

      .problem-list {
        display: grid;
        gap: var(--space-md);
        margin: 0;
        padding: 0;
        list-style: none;
      }

      .problem-list li {
        padding: var(--space-lg);
      }

      .problem-list h3 {
        margin: 0 0 var(--space-xs);
        font-size: 1.0625rem;
      }

      .problem-list p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .flow-steps {
        display: grid;
        gap: var(--space-md);
      }

      .step {
        display: flex;
        gap: var(--space-md);
        align-items: flex-start;
      }

      .step-num {
        flex-shrink: 0;
        width: 2rem;
        height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-full);
        background: var(--gradient-primary);
        color: var(--palette-white);
        font-weight: 700;
        font-size: 0.875rem;
      }

      .step h3 {
        margin: 0 0 0.25rem;
        font-size: 1rem;
      }

      .step p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .features {
        display: grid;
        gap: var(--space-md);
        grid-template-columns: 1fr;
      }

      @media (min-width: 700px) {
        .features {
          grid-template-columns: repeat(2, 1fr);
        }

        .compare {
          grid-template-columns: 1fr 1fr;
        }
      }

      .feature h3 {
        margin: 0 0 var(--space-xs);
        font-size: 1.0625rem;
      }

      .feature p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .not-list {
        margin: 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: var(--space-sm);
      }

      .not-list li {
        padding: var(--space-md) var(--space-lg);
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .compare {
        display: grid;
        gap: var(--space-md);
        grid-template-columns: 1fr;
      }

      .compare-col h3 {
        margin: 0 0 var(--space-md);
        font-size: 1rem;
      }

      .compare-col ul {
        margin: 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: var(--space-sm);
      }

      .compare-col li {
        font-size: 0.9375rem;
        color: var(--color-text-muted);
        line-height: 1.5;
        padding-left: 1rem;
        position: relative;
      }

      .compare-col--without li::before {
        content: '–';
        position: absolute;
        left: 0;
        color: var(--color-text-subtle);
      }

      .compare-col--with li::before {
        content: '+';
        position: absolute;
        left: 0;
        color: var(--color-accent);
        font-weight: 600;
      }

      .cta-block {
        margin-top: var(--space-2xl);
        padding: var(--space-2xl) var(--space-xl);
        border-radius: var(--radius-lg);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        text-align: center;
      }

      .cta-block h2 {
        margin: 0 0 var(--space-sm);
        font-size: clamp(1.375rem, 3vw, 1.75rem);
      }

      .cta-block p {
        margin: 0 auto var(--space-xl);
        max-width: 48ch;
        color: var(--color-text-muted);
        font-size: 1rem;
        line-height: 1.6;
      }
    `,
  ];

  render() {
    return html`
      <section class="hero animate-fade-up">
        <div class="hero-content">
          <span class="eyebrow">Proyecto de AlejandroSN</span>
          <h1><span class="brand-name">Traviapp</span></h1>
          <p class="tagline">El link-in-bio que crece contigo.</p>
          <p class="hero-lead">
            Perfil, enlaces, secciones, suscriptores y analítica — todo en una página que se
            siente como tuya. No es un listado de botones: es tu marca, en un solo enlace.
          </p>
          <div class="hero-actions">
            <ui-button href=${CALENDAR_URL} variant="primary">Reservar una llamada</ui-button>
          </div>
        </div>
      </section>

      <section>
        <h2 class="section-title">El problema que resuelve</h2>
        <p class="section-lead">
          Un link-in-bio no debería parecer de alquiler. Si creces, la página tiene que crecer
          contigo: más enlaces, más contenido, más control sobre tu audiencia.
        </p>
        <ul class="problem-list">
          <li class="card">
            <h3>Todas las bios se parecen</h3>
            <p>
              Plantilla, cuatro botones y el mismo gris. Cuesta reconocer la marca detrás del
              enlace. Traviapp parte de tu look, no de un molde.
            </p>
          </li>
          <li class="card">
            <h3>Te quedas corto cuando dejas de ser “solo links”</h3>
            <p>
              Quieres texto, imagen, un vídeo, un CTA. La bio típica no da para una página que
              explique quién eres.
            </p>
          </li>
          <li class="card">
            <h3>Tu audiencia vive en el algoritmo de otro</h3>
            <p>
              Si Instagram cambia las reglas, se lleva el alcance. Capturar emails en tu propia
              página corta esa dependencia.
            </p>
          </li>
        </ul>
      </section>

      <section>
        <h2 class="section-title">Cómo funciona</h2>
        <p class="section-lead">De la marca al enlace que compartes, sin configurar infraestructura.</p>
        <div class="flow-steps">
          ${STEPS.map(
            (step, i) => html`
              <div class="step">
                <span class="step-num">${i + 1}</span>
                <div>
                  <h3>${step.title}</h3>
                  <p>${step.body}</p>
                </div>
              </div>
            `,
          )}
        </div>
      </section>

      <section>
        <h2 class="section-title">Qué incluye</h2>
        <p class="section-lead">Lo que necesitas para una página de marca, no un experimento de botones.</p>
        <div class="features">
          ${FEATURES.map(
            (f) => html`
              <article class="card feature">
                <h3>${f.title}</h3>
                <p>${f.body}</p>
              </article>
            `,
          )}
        </div>
      </section>

      <section>
        <h2 class="section-title">Qué no es Traviapp</h2>
        <p class="section-lead">Para evitar expectativas equivocadas.</p>
        <ul class="not-list">
          ${NOT_THIS.map((item) => html`<li class="card">${item}</li>`)}
        </ul>
      </section>

      <section>
        <h2 class="section-title">Sin Traviapp vs con Traviapp</h2>
        <div class="compare">
          <div class="card compare-col compare-col--without">
            <h3>Sin Traviapp</h3>
            <ul>
              ${COMPARE.map((row) => html`<li>${row.without}</li>`)}
            </ul>
          </div>
          <div class="card compare-col compare-col--with">
            <h3>Con Traviapp</h3>
            <ul>
              ${COMPARE.map((row) => html`<li>${row.with}</li>`)}
            </ul>
          </div>
        </div>
      </section>

      <div class="cta-block animate-fade-up">
        <h2>¿Quieres una página así?</h2>
        <p>
          Traviapp es producto propio: perfil, contenido rico, suscriptores y SEO en un solo
          enlace. Si encaja con lo que necesitas, lo vemos en una llamada.
        </p>
        <ui-button href=${CALENDAR_URL} variant="primary">Reservar una llamada</ui-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'traviapp-page': TraviappPage;
  }
}
